import { Avatar, MenuOption, usePopoverPosition } from "@design";
import { useQuery } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { UserSearchResult } from "@/api";
import { searchUsersOptions } from "@/api";

/**
 * Search-and-pick a platform user by name or email (for the membership Access
 * panel's add flow). A debounced query against /auth/users/search drives a
 * listbox with full keyboard nav (↓/↑/Enter/Escape); picking fills the field.
 * Focus stays on the input (the APG combobox pattern, same as the design
 * system's Select).
 */
export function UserSearchCombobox({
  onSelect,
  excludeUuids = [],
  clearOnSelect = false,
}: Readonly<{
  onSelect: (user: UserSearchResult) => void;
  excludeUuids?: string[];
  /** Empty the field after a pick (for "add another" flows) instead of showing the picked user. */
  clearOnSelect?: boolean;
}>) {
  const { t } = useTranslation("build");
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [debounced, setDebounced] = useState("");
  const anchorRef = useRef<HTMLDivElement>(null);
  const listId = `user-search-${useId()}`;

  useEffect(() => {
    const id = setTimeout(() => setDebounced(input.trim()), 250);
    return () => clearTimeout(id);
  }, [input]);

  const search = useQuery({
    ...searchUsersOptions({ query: { q: debounced } }),
    enabled: debounced.length > 0,
  });
  const results = (search.data?.data ?? []).filter((u) => !excludeUuids.includes(u.uuid));
  const searching = debounced.length > 0 && search.isFetching;
  const showPanel = open && debounced.length > 0;
  // Keyed on showPanel, not open: the panel mounts only once the debounced query is set,
  // and the hook measures it on the transition to open.
  const { panelRef, style } = usePopoverPosition<HTMLDivElement>({
    isOpen: showPanel,
    anchorRef,
    matchAnchorWidth: true,
  });

  // Reset the highlight whenever the result set changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset is driven by the result set changing (debounced) and its size.
  useEffect(() => setHighlighted(0), [results.length, debounced]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!anchorRef.current?.contains(e.target as Node) && !panelRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, panelRef]);

  const pick = (user: UserSearchResult) => {
    onSelect(user);
    setInput(clearOnSelect ? "" : user.name || user.email);
    if (clearOnSelect) setDebounced("");
    setOpen(false);
  };

  const clear = () => {
    setInput("");
    setDebounced("");
    onSelect({ uuid: "", email: "", name: "" } as UserSearchResult);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!showPanel || results.length === 0) {
      if (e.key === "Escape") setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => (h + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => (h - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(results[highlighted] as UserSearchResult);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // div[role=listbox] > button[role=option] (MenuOption) is the WAI-ARIA APG combobox
  // pattern; focus stays on the combobox input (the same as the design system's Select).
  const panel = (
    <div
      ref={panelRef}
      style={style}
      id={listId}
      role="listbox"
      aria-label={t("access.addPerson")}
      className="z-[110] max-h-64 overflow-auto rounded-xl border border-border-input bg-surface-elevated px-1 py-1 shadow-lg"
    >
      {searching && results.length === 0 && (
        <div className="flex items-center gap-2 px-3 py-2 text-content-tertiary">
          <Loader2 size={14} className="animate-spin" aria-hidden />
          <span className="[font:var(--type-body-sm)]">{t("access.searching")}</span>
        </div>
      )}
      {!searching && results.length === 0 && (
        <div className="px-3 py-2 text-content-tertiary [font:var(--type-body-sm)]">{t("access.noUsersFound")}</div>
      )}
      {results.map((user, i) => (
        <MenuOption
          key={user.uuid}
          id={`${listId}-opt-${i}`}
          role="option"
          highlighted={i === highlighted}
          onHighlight={() => setHighlighted(i)}
          onSelect={() => pick(user)}
          icon={
            <Avatar
              initials={(user.name || user.email).slice(0, 2).toUpperCase()}
              src={user.avatarUrl ?? undefined}
              size="sm"
            />
          }
          description={user.email}
        >
          {user.name || user.email}
        </MenuOption>
      ))}
    </div>
  );

  return (
    <div ref={anchorRef} className="relative flex-1">
      <input
        type="text"
        role="combobox"
        aria-expanded={showPanel}
        aria-controls={showPanel ? listId : undefined}
        aria-activedescendant={showPanel && results.length > 0 ? `${listId}-opt-${highlighted}` : undefined}
        aria-autocomplete="list"
        aria-label={t("access.addPerson")}
        placeholder={t("access.emailPlaceholder")}
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          setOpen(true);
        }}
        onFocus={() => input && setOpen(true)}
        onKeyDown={onKeyDown}
        className="block w-full rounded-lg border border-border-input bg-surface-elevated h-10 pl-3 pr-8 text-content-primary placeholder:text-content-tertiary btn-focus-ring"
      />
      {input && (
        <button
          type="button"
          aria-label={t("access.clearSearch")}
          onClick={clear}
          className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer rounded text-content-tertiary hover:text-content-primary"
        >
          <X size={14} aria-hidden />
        </button>
      )}
      {showPanel && panel}
    </div>
  );
}
